/**
 * ManageMenu — every leadership tool behind one nav entry.
 * @file src/web/shared/components/ManageMenu.tsx
 *
 * @llm-rule WHEN: A Company Admin or Head needs the tools for running the company
 * @llm-rule NOTE: These used to be split across the nav (Offices, Company) and
 *   the avatar menu (Authorities, Monthly report) with no rule about which went
 *   where, so a Company Admin had to hunt in two places. One menu, one rule: running the
 *   company is here, your own day is in the nav, you are in the avatar.
 * @llm-rule AVOID: Rendering this inside the `overflow-x-auto` nav. That makes
 *   overflow-y compute to auto as well and the dropdown is clipped to a ~32px
 *   strip — present in the DOM, invisible on screen.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  CalendarRange,
  ChevronDown,
  MapPin,
  Receipt,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { rankOf, type OrgRole } from '../domain';

export interface ManageItem {
  label: string;
  href: string;
  hint: string;
  icon: typeof Users;
  /** Company-Admin-only entries, e.g. handing out authorities. */
  adminOnly?: boolean;
  /**
   * Lowest rank the destination actually admits.
   *
   * Every entry MUST carry this. Without it the menu offered a Manager and an
   * Employee links to pages that redirect them straight back to the clock —
   * a menu full of dead ends reads as a broken app, not as a permission.
   */
  minRole: OrgRole;
}

/** The tools for running the company, in the order a Company Admin reaches for them. */
export const MANAGE_ITEMS: ManageItem[] = [
  {
    label: 'Company today',
    minRole: 'MANAGER',
    href: '/oversight',
    hint: 'Who is in, who is flagged, what is overdue',
    icon: Building2,
  },
  {
    label: 'People',
    minRole: 'HEAD',
    href: '/team',
    hint: 'Add someone, move them under a lead, reset a password or face',
    icon: Users,
  },
  {
    label: 'Offices',
    minRole: 'HEAD',
    href: '/locations',
    hint: 'Where clocking in counts as on-site',
    icon: MapPin,
  },
  {
    label: 'Monthly report',
    minRole: 'MANAGER',
    href: '/reports',
    hint: 'Attendance, leave, flags and tasks month by month',
    icon: CalendarRange,
  },
  {
    label: 'Authorities',
    minRole: 'COMPANY_ADMIN',
    href: '/authorities',
    hint: 'Who may approve leave and raise flags on your behalf',
    icon: ShieldCheck,
    adminOnly: true,
  },
  {
    label: 'Company settings',
    minRole: 'COMPANY_ADMIN',
    href: '/settings',
    hint: 'Working day, weekly offs, leave a year, holiday calendar',
    icon: SlidersHorizontal,
    adminOnly: true,
  },
  {
    label: 'Payslips',
    minRole: 'HEAD',
    href: '/payslips',
    hint: 'Generate pay for a month range, or read what has been issued',
    icon: Receipt,
  },
];

/**
 * The manage entries this rank can actually open.
 *
 * Gated by what each destination admits, not by "is this person senior".
 * An Employee gets an empty list, which is what removes the group entirely.
 */
export function manageItemsFor(role: OrgRole): ManageItem[] {
  return MANAGE_ITEMS.filter((item) => {
    if (item.adminOnly) return role === 'COMPANY_ADMIN';
    return rankOf(role) >= rankOf(item.minRole);
  });
}

export function ManageMenu({ role }: { role: OrgRole }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const items = manageItemsFor(role);
  const active = items.some((item) => location.pathname.startsWith(item.href));

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Any navigation closes it.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-all ${
          active || open
            ? 'bg-paper-sunk text-ink shadow-[inset_0_0_0_1px_var(--rule)]'
            : 'text-ink-faint hover:bg-paper-sunk/60 hover:text-ink'
        }`}
      >
        Manage
        <ChevronDown
          className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2.2}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[290px] overflow-hidden rounded-2xl border border-rule bg-paper p-1.5"
          style={{ boxShadow: 'var(--elev-3)' }}
        >
          {items.map((item) => {
            const isHere = location.pathname.startsWith(item.href);
            return (
              <Link
                key={item.href}
                to={item.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={`flex items-start gap-2.5 rounded-xl px-2.5 py-2 transition-colors ${
                  isHere ? 'bg-paper-sunk' : 'hover:bg-paper-warm'
                }`}
              >
                <item.icon
                  className="mt-0.5 size-4 flex-none text-ink-faint"
                  strokeWidth={1.8}
                />
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-ink">{item.label}</span>
                  <span className="block text-[11.5px] leading-snug text-ink-faint">
                    {item.hint}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
